import * as React from "react";

import { type TargetEntityType } from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";

import { CustomFieldValuesTable } from "./CustomFieldValuesTable";

export interface CustomFieldValuesSectionProps {
  /** Entity type whose Ready fields are offered. */
  entityType: TargetEntityType;

  /** Org that will own the new entity (the Ready defs are read from it). */
  orgId: string;

  /** Draft values by field name, owned by the host form. */
  values: Record<string, unknown>;

  /** Report the next draft map so the host can submit it with the entity. */
  onChange: (values: Record<string, unknown>) => void;

  /**
   * Section label, rendered above the table in the host form's own idiom (so it
   * matches sibling labels like "Name (optional)"). Passed in — rather than
   * rendered by the host directly — so it shares this section's gating: when the
   * flag is off or no Ready fields exist, the whole section (header included) is
   * absent, not a dangling label.
   */
  header?: React.ReactNode;
}

/**
 * Custom Fields section for an entity *create* form. Controlled: the host form
 * owns `values` and folds them into its create request, which the backend
 * accepts as initial custom-field values.
 *
 * Reuses the detail-page {@link CustomFieldValuesTable} in draft mode — each
 * set/clear just rewrites the host's draft map rather than committing to a
 * server. A value only enters the map once it passes the table's committable
 * check, so the submitted map is always valid. The table's own heading is
 * suppressed in favour of the host-styled `header`.
 *
 * Renders nothing when the `release-custom-fields` flag is off or the entity
 * type has no Ready fields, so a create form gains no empty section.
 */
export function CustomFieldValuesSection({
  entityType,
  orgId,
  values,
  onChange,
  header,
}: CustomFieldValuesSectionProps) {
  const enabled = useCheckFeatureFlag("release-custom-fields");
  const { data: readyFields } = useReadyCustomFields({
    entityType,
    orgId,
    enabled,
  });

  function handleSet(fieldName: string, value: unknown) {
    onChange({ ...values, [fieldName]: value });
  }

  function handleClear(fieldName: string) {
    // Drop the key entirely so the create request omits it, rather than
    // sending an explicit null the server would treat as a value.
    const next = { ...values };
    delete next[fieldName];
    onChange(next);
  }

  if (!enabled || !readyFields || readyFields.length === 0) {
    return null;
  }

  return (
    <div>
      {header}
      <CustomFieldValuesTable
        entityType={entityType}
        readyFields={readyFields}
        values={values}
        onSet={handleSet}
        onClear={handleClear}
        variant="plain"
        heading={null}
      />
    </div>
  );
}
